import { ErrorMessage } from '@hookform/error-message';
import { type FC, useContext, useEffect, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Select from 'react-dropdown-select';
import { Controller, useForm } from 'react-hook-form';
import { MultiSelect, type Option } from 'react-multi-select-component';
import PhoneInput from 'react-phone-input-2';
import { unknownErrorMessage } from '../../../constants/generalData';
import { UserPropertiesContext } from '../../../modules/context/userPropertiesContext';
import { AccessCodeDurationUnit } from '../../../modules/enums/types';
import {
  useAuth,
  useMutationRequest,
  useNotifications,
  useRequest,
} from '../../../modules/hooks';
import type {
  RoomLocks,
  TemporaryAccessCodes,
  TemporaryAccessCodesPayload,
} from '../../../modules/interfaces';
import { endpoints } from '../../../modules/mappers/urls';
import type { SelectType } from '../MessageManagerModal';

interface UserDetailsModalProps {
  show: boolean;
  handleClose: () => void;
  isEdit?: boolean;
  defaultTemporaryAccessCode?: TemporaryAccessCodes;
  onRefresh: () => void;
}

export const AddTemporaryAccessCodeModal: FC<UserDetailsModalProps> = ({
  show,
  handleClose,
  isEdit = false,
  defaultTemporaryAccessCode,
  onRefresh,
}) => {
  const { credentialsInfo } = useAuth()!;
  const [locksData, setLocksData] = useState<RoomLocks[]>([]);
  const [temporaryAccessCodeId, setTemporaryAccessCodeId] = useState<string>('');
  const [selectedTimeUnit, setSelectedTimeUnit] = useState<Option[]>([
    { value: AccessCodeDurationUnit.MINUTES, label: 'Minutes' },
  ]);
  const { selectedProperty } = useContext(UserPropertiesContext)!;
  const { setSimpleToasts } = useNotifications()!;

  const {
    register,
    handleSubmit,
    control,
    reset,
    trigger,
    formState: { errors },
  } = useForm<TemporaryAccessCodesPayload>({
    mode: 'onChange',
    defaultValues: {
      duration: 0,
      locksIds: [] as string[],
      durationUnit: AccessCodeDurationUnit.MINUTES,
      reservationId: '',
      guestFirstName: '',
      guestLastName: '',
      guestEmail: '',
      phoneNumber: '',
    },
  });

  const lockUrl = `${endpoints.LOCKS}/room-locks`;
  const [{ data: locksResult, loading: loadingLocks, error: locksErrors }] = useRequest<
    RoomLocks[]
  >(lockUrl, 'get', {
    authToken: credentialsInfo?.token,
    propertyId: selectedProperty?.property.uuid,
  });

  const temporaryAccessCodeUrl = `${endpoints.LOCKS}/temporary-access-code`;
  const [
    { data: addAccessCodeResult, loading: loadingAccessCode, error: errorAccessCode },
    addTemporaryAccessCode,
  ] = useMutationRequest<RoomLocks[]>(
    temporaryAccessCodeUrl,
    'post',
    {
      authToken: credentialsInfo?.token,
      propertyId: selectedProperty?.property.uuid,
    },
    { manual: true },
  );

  const updateTempAccessCodeUrl = `${endpoints.LOCKS}/temporary-access-code/${temporaryAccessCodeId}`;
  const [
    {
      data: updateAccessCodeResult,
      loading: loadingUpdateAccessCode,
      error: errorUpdateAccessCode,
    },
    updateTemporaryAccessCode,
  ] = useMutationRequest<RoomLocks[]>(
    updateTempAccessCodeUrl,
    'put',
    {
      authToken: credentialsInfo?.token,
      propertyId: selectedProperty?.property.uuid,
    },
    { manual: true },
  );

  const [selectedLocks, setSelectedLocks] = useState<Option[]>([]);

  const getPermissionDropDownData = () => {
    const dropDownList: SelectType[] = [];

    locksData.forEach((item) => {
      dropDownList.push({
        value: item?.uuid || '',
        label: item?.lockName || '',
      });
    });
    return dropDownList;
  };

  const addTemporaryAccessCodeHandler = async (data: TemporaryAccessCodesPayload) => {
    const locksIds = selectedLocks.map((lock) => lock.value);

    let guestPhoneNumber: string | undefined = '';
    const currentPhoneNumber = data?.phoneNumber;
    if (
      !currentPhoneNumber ||
      currentPhoneNumber.trim() === '' ||
      currentPhoneNumber.length < 4
    ) {
      guestPhoneNumber = undefined;
    } else {
      guestPhoneNumber = currentPhoneNumber;
    }

    const [unit] = selectedTimeUnit;

    const payload = {
      duration: Number(data?.duration),
      roomLockIds: locksIds,
      guestFirstName: data?.guestFirstName,
      guestLastName: data?.guestLastName,
      guestEmail: data?.guestEmail,
      phoneNumber: guestPhoneNumber,
      propertyId: selectedProperty?.property.uuid,
      durationUnit: unit.value,
    };

    if (isEdit) {
      updateTemporaryAccessCode(payload);
    } else {
      addTemporaryAccessCode(payload);
    }
  };

  const durationUnitToOption = (unit: AccessCodeDurationUnit): Option => {
    const key = Object.keys(AccessCodeDurationUnit).find(
      (k) => AccessCodeDurationUnit[k as keyof typeof AccessCodeDurationUnit] === unit,
    );

    if (!key) {
      return { value: unit, label: '' };
    }

    const label = key.charAt(0) + key.slice(1).toLowerCase();

    return { value: unit, label };
  };

  const onCancel = async () => {
    handleClose();
    setSelectedLocks([]);
    setSelectedTimeUnit([
      {
        value: AccessCodeDurationUnit.MINUTES,
        label: 'Minutes',
      },
    ]);
    reset({
      duration: 0,
      locksIds: [] as string[],
      reservationId: '',
      guestFirstName: '',
      guestLastName: '',
      guestEmail: '',
      phoneNumber: '',
    });
  };

  const onSubmit = async (data: any) => {
    const validated = await trigger([
      'duration',
      'guestFirstName',
      'guestLastName',
      'guestEmail',
    ]);

    if (validated) {
      addTemporaryAccessCodeHandler(data);
    }
  };

  const optionUnits = (): Option[] => {
    const accessCodeDurationOptions = Object.entries(AccessCodeDurationUnit).map(
      ([key, value]) => ({
        value,
        label: key.charAt(0) + key.slice(1).toLowerCase(),
      }),
    );
    return accessCodeDurationOptions;
  };

  useEffect(() => {
    if (locksResult) {
      setLocksData(locksResult?.result);
    }

    if (locksErrors) {
      setSimpleToasts({
        message: locksErrors?.response?.data.error.message || unknownErrorMessage,
        type: 'danger',
        show: true,
      });
    }
  }, [locksResult, locksErrors]);

  useEffect(() => {
    if (defaultTemporaryAccessCode && show) {
      reset({
        guestEmail: defaultTemporaryAccessCode.guestEmail,
        duration: defaultTemporaryAccessCode.duration,
        phoneNumber: defaultTemporaryAccessCode.phoneNumber,

        guestFirstName: defaultTemporaryAccessCode.guestFirstName,
        guestLastName: defaultTemporaryAccessCode.guestLastName,
        durationUnit: defaultTemporaryAccessCode?.durationUnit,
      });

      if (defaultTemporaryAccessCode?.roomLocks) {
        const selectedRoomLocks: Option[] = defaultTemporaryAccessCode?.roomLocks.map(
          (item) => ({
            value: item?.uuid || '',
            label: item?.lockName || '',
          }),
        );
        setSelectedLocks(selectedRoomLocks);
      }

      if (defaultTemporaryAccessCode?.durationUnit) {
        const option = durationUnitToOption(defaultTemporaryAccessCode.durationUnit);

        setSelectedTimeUnit([option]);
      }

      setTemporaryAccessCodeId(defaultTemporaryAccessCode.uuid);
    }
  }, [defaultTemporaryAccessCode, show]);

  useEffect(() => {
    if (addAccessCodeResult || updateAccessCodeResult) {
      onRefresh();
      onCancel();
    }

    if (errorAccessCode) {
      setSimpleToasts({
        message: errorAccessCode?.response?.data.error.message || unknownErrorMessage,
        type: 'danger',
        show: true,
      });
    }
    if (errorUpdateAccessCode) {
      setSimpleToasts({
        message:
          errorUpdateAccessCode?.response?.data.error.message || unknownErrorMessage,
        type: 'danger',
        show: true,
      });
    }
  }, [
    addAccessCodeResult,
    updateAccessCodeResult,
    errorAccessCode,
    errorUpdateAccessCode,
  ]);

  return (
    <Modal
      show={show}
      onHide={handleClose}
      dialogClassName="post-editor-modal modal-200w"
      backdrop="static"
      centered
    >
      <Modal.Header>
        <Modal.Title className="editor-title">
          {`${isEdit ? `Edit` : `Create`} Temporary Access Code `}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row>
            <Col xs={7}>
              <Form.Label className="editor-title-label">Duration</Form.Label>
              <Form.Control
                className="editor-control "
                placeholder="Enter the access code duration"
                {...register('duration', {
                  required: 'This is required.',
                  min: {
                    value: 1,
                    message: 'Value must be greater than 0.',
                  },
                })}
              />
              <ErrorMessage
                errors={errors}
                name="duration"
                render={({ message }) => <span className="error-text">{message}</span>}
              />
            </Col>
            <Col xs={5}>
              <Form.Label className="editor-title-label">Unit</Form.Label>

              <Select
                options={optionUnits()}
                values={selectedTimeUnit}
                onChange={(values) => {
                  setSelectedTimeUnit(values);
                }}
              />
            </Col>
          </Row>
          <Row className="mt-16px">
            <Col>
              <Form.Label className="editor-title-label">Locks</Form.Label>
              <MultiSelect
                disableSearch
                hasSelectAll={false}
                options={getPermissionDropDownData()}
                value={selectedLocks}
                labelledBy="Default"
                onChange={setSelectedLocks}
                isLoading={loadingLocks}
              />
            </Col>
          </Row>
          <Row className="mt-16px">
            <Col>
              <Form.Label className="editor-title-label">Email</Form.Label>
              <Form.Control
                className="editor-control"
                placeholder="Enter a guest email..."
                {...register('guestEmail', {
                  required: 'This is required',
                })}
                disabled={isEdit}
              />
              <ErrorMessage
                errors={errors}
                name="guestEmail"
                render={({ message }) => <span className="error-text">{message}</span>}
              />
            </Col>
          </Row>
          <Row className="mt-16px">
            <Col>
              <Form.Label className="editor-title-label">First Name</Form.Label>
              <Form.Control
                className="editor-control"
                placeholder="Enter guest first name..."
                {...register('guestFirstName', {
                  required: 'This is required',
                })}
                disabled={isEdit}
              />
              <ErrorMessage
                errors={errors}
                name="guestFirstName"
                render={({ message }) => <span className="error-text">{message}</span>}
              />
            </Col>
          </Row>
          <Row className="mt-16px">
            <Col>
              <Form.Label className="editor-title-label">Last Name</Form.Label>
              <Form.Control
                className="editor-control"
                placeholder="Enter guest last name..."
                {...register('guestLastName', { required: 'This is required' })}
                disabled={isEdit}
              />
              <ErrorMessage
                errors={errors}
                name="guestLastName"
                render={({ message }) => <span className="error-text">{message}</span>}
              />
            </Col>
          </Row>
          <Row className="mt-16px">
            <Col>
              <Form.Label className="editor-title-label">Phone Number</Form.Label>

              <Controller
                name="phoneNumber"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <PhoneInput
                    country="us"
                    value={value}
                    onChange={(phoneValue) => {
                      onChange(
                        phoneValue.startsWith('+') ? phoneValue : `+${phoneValue}`,
                      );
                    }}
                    inputStyle={{ width: '100%' }}
                    countryCodeEditable={false}
                    disabled={isEdit}
                  />
                )}
              />
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer
        style={{
          display: 'flex',
          justifyContent: 'end',
        }}
      >
        <Button className="cancel-button" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          className="save-button"
          onClick={handleSubmit(onSubmit)}
          disabled={loadingUpdateAccessCode || loadingAccessCode}
        >
          {loadingUpdateAccessCode || loadingAccessCode ? 'Loading...' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
